import { analytics } from '@/features/analytics/events'
import { routes } from '@/pages'
import { Button, Text } from '@/ui-kit'
import { formatNumber } from '@/utils'
import { Trans } from '@lingui/react/macro'
import { useNavigate } from 'react-router-dom'
import { useMissionsUrl } from '../context/MissionsUrlProvider'

export const MissionNotAvailable = ({
  projectId,
  title,
  description,
  isAuthorized,
  rewardConfig
}: {
  projectId: string
  title: string
  description: string
  isAuthorized: boolean
  rewardConfig: {
    type: string
    amount: number
  }
}) => {
  const navigate = useNavigate()
  const { missionsUrl } = useMissionsUrl()

  const upToUsd = `$${formatNumber(rewardConfig.amount, 2)}`
  const upToData = formatNumber(rewardConfig.amount, 0)

  const goToLogin = () => {
    const returnTo = `${location.pathname}${location.search}`
    analytics.missionOnboardingStepCTA(projectId, 'Sign In')
    analytics.signInFromMissionOnBoarding(projectId)
    navigate(`${routes.login.path}?redirect=${encodeURIComponent(returnTo)}`)
  }

  return (
    <div className="max-mobile:max-w-[calc(100vw-40px)] m-auto mt-12 flex w-[720px] flex-col">
      {rewardConfig.amount > 0 && (
        <div className="max-mobile:px-0 relative -mt-12 px-10">
          <div className="max-mobile:text-[22px]/[24px] max-mobile:py-4 max-mobile:pt-12 pt-9 pb-4 text-[28px]/[32px] font-semibold">
            {rewardConfig.type === 'usd' ? (
              <>
                <Trans>
                  Complete the mission{' '}
                  <span className="text-orange max-mobile:max-w-[200px] max-mobile:mt-2 block">
                    to earn up to {upToUsd}!
                  </span>
                </Trans>
              </>
            ) : (
              <>
                <Trans>
                  Complete the mission to earn up to{' '}
                  <span className="text-orange max-mobile:max-w-[200px] max-mobile:mt-2 block">
                    {upToData} $DATA points for airdrop!
                  </span>
                </Trans>
              </>
            )}
          </div>
          {rewardConfig.type !== 'usd' ? (
            <img
              src="/images/dh-coins.png"
              role="presentation"
              className="max-mobile:w-[110px] max-mobile:right-4 absolute right-10 bottom-0 w-[155px]"
            />
          ) : (
            <img
              src="/images/usd-earnings.webp"
              role="presentation"
              className="max-mobile:w-[135px] max-mobile:bottom-[-40px] max-mobile:right-[-10px] absolute right-10 -bottom-14 -z-[1] w-[210px]"
            />
          )}
        </div>
      )}
      <div className="bg-background max-mobile:px-4 max-mobile:py-8 flex flex-col items-center justify-center gap-4 rounded-2xl border border-solid border-gray-100 px-10 py-12 text-center">
        {isAuthorized ? (
          <div className="flex flex-col items-center justify-center gap-4 text-center">
            <div className="flex w-full flex-col items-center justify-center gap-2 border-b border-gray-100 pb-7">
              <div className="text-subtitle text-gray-80 font-semibold">{title}</div>
              <div className="text-body text-gray-80 max-w-[600px] whitespace-pre-line">{description}</div>
            </div>

            <Text type="h5" className="text-gray-70 mt-2">
              <Trans>Mission at Full Capacity</Trans>
            </Text>
            <div className="flex flex-col items-center justify-center">
              <Text type="body1-regular" className="text-gray-80">
                <Trans>
                  All participation for this mission has been allocated. Availability may change, so please check again
                  later.
                </Trans>
              </Text>
            </div>

            <Button size="small" variant="bordered" onClick={() => navigate(missionsUrl)} className="mt-2">
              <Trans>Back to Missions</Trans>
            </Button>
          </div>
        ) : (
          <div className="flex w-full flex-col items-center gap-3 border-b border-gray-100 pb-7 text-center">
            <div className="flex flex-col items-center justify-center gap-2">
              <Text type="h5" className="text-gray-70">
                {title}
              </Text>
              <div className="text-body text-gray-80 max-w-[600px] whitespace-pre-line">{description}</div>
            </div>
            <Text type="body1-regular" className="text-gray-80 max-mobile:mt-4 mt-6">
              <Trans>Complete the steps below to unlock this mission.</Trans>
            </Text>
            {!isAuthorized && (
              <Button className="mt-2 w-[300px]" onClick={goToLogin}>
                <Trans>Sign In</Trans>
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
